import React, { useMemo, useState } from 'react';
import Card from '../components/Card';
import Button from '../components/Button';
import { inputStyle } from '../components/FormField';
import * as optimizationApi from '../services/optimizationApi';

const SHOW_OPTIONS = [10, 25, 50, 'All'];

function StatBlock({ label, value, tone }) {
  const toneColor = tone === 'danger' ? 'var(--color-danger)' : tone === 'success' ? 'var(--color-success)' : 'var(--color-text-primary)';
  return (
    <div>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{label}</div>
      <div className="data-figure" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: toneColor }}>{value}</div>
    </div>
  );
}

function ShowCountSelect({ value, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
      Show
      <select value={value} onChange={(e) => onChange(e.target.value === 'All' ? 'All' : Number(e.target.value))} style={{ ...inputStyle, width: 'auto' }}>
        {SHOW_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

export default function OptimizationPage() {
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const [safetyShowCount, setSafetyShowCount] = useState('All');
  const [safetySortDir, setSafetySortDir] = useState('desc');
  const [priorityShowCount, setPriorityShowCount] = useState('All');

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    try {
      const data = await optimizationApi.runOptimization(14);
      setResult(data);
    } catch (err) {
      setError(err.message || 'Optimization failed.');
    } finally {
      setRunning(false);
    }
  };

  const sortedSafetyStock = useMemo(() => {
    if (!result) return [];
    const rows = [...result.safetyStockOptimization];
    rows.sort((a, b) => (safetySortDir === 'asc' ? a.delta - b.delta : b.delta - a.delta));
    return safetyShowCount === 'All' ? rows : rows.slice(0, safetyShowCount);
  }, [result, safetySortDir, safetyShowCount]);

  const safetyTotals = useMemo(() => {
    const rows = result?.safetyStockOptimization || [];
    return {
      currentSafetyStock: rows.reduce((s, r) => s + r.currentSafetyStock, 0),
      optimizedSafetyStock: rows.reduce((s, r) => s + r.optimizedSafetyStock, 0),
      delta: rows.reduce((s, r) => s + r.delta, 0),
    };
  }, [result]);

  const pp = result?.productionPriorityOptimization;
  const improved = pp && pp.optimized.priorityWeightedUnmet < pp.baseline.priorityWeightedUnmet;
  const displayedPriority = useMemo(() => {
    if (!pp) return [];
    return priorityShowCount === 'All' ? pp.scoredDemands : pp.scoredDemands.slice(0, priorityShowCount);
  }, [pp, priorityShowCount]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>AI Optimization Engine</h1>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Improves on two existing heuristics: safety stock that accounts for demand
            variability (not just its average), and a production schedule ordered by real
            urgency (not just order age).
          </p>
        </div>
        <Button onClick={handleRun} disabled={running}>{running ? 'Optimizing…' : 'Run optimization'}</Button>
      </div>

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}

      {result && (
        <>
          <Card title="Safety Stock Optimization">
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
              Uses the standard inventory-science formula (service level × demand volatility ×
              √lead time) instead of average demand alone — two designs with the same average
              can need very different buffers depending on how unpredictable they are.
            </p>
            <div className="kpi-row" style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
              <ShowCountSelect value={safetyShowCount} onChange={setSafetyShowCount} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                Sort by change
                <select value={safetySortDir} onChange={(e) => setSafetySortDir(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </label>
            </div>
            <div className="table-scroll">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
                  {['Design', 'Avg Weekly Demand', 'Volatility (σ)', 'Current Safety Stock', 'Optimized', 'Change'].map((h) => (
                    <th key={h} style={{ padding: '8px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedSafetyStock.map((s) => (
                  <tr key={s.design_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{s.designName}</td>
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{s.avgWeeklyDemand}</td>
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{s.demandStdDev}</td>
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{s.currentSafetyStock}</td>
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>{s.optimizedSafetyStock}</td>
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', color: s.delta > 0 ? 'var(--color-warning)' : s.delta < 0 ? 'var(--color-success)' : 'var(--color-text-muted)' }}>
                      {s.delta > 0 ? '+' : ''}{s.delta}
                    </td>
                  </tr>
                ))}
                {sortedSafetyStock.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)' }}>Not enough dispatch history yet (needs 4+ weeks per design).</td></tr>
                )}
                {sortedSafetyStock.length > 0 && (
                  <tr style={{ borderTop: '2px solid var(--color-border)', fontWeight: 700 }}>
                    <td style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>Total</td>
                    <td />
                    <td />
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{safetyTotals.currentSafetyStock}</td>
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)' }}>{safetyTotals.optimizedSafetyStock}</td>
                    <td className="data-figure" style={{ padding: '8px 4px', fontSize: 'var(--font-size-sm)', color: safetyTotals.delta > 0 ? 'var(--color-warning)' : 'var(--color-success)' }}>
                      {safetyTotals.delta > 0 ? '+' : ''}{safetyTotals.delta}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          </Card>

          {pp && (
            <Card title="Production Priority Optimization">
              <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                Scores each design's production need on aging, rising demand, and stock risk —
                not order age alone — then compares which allocation better protects
                high-priority demand when capacity is tight. Rows with urgent stock (negative or
                low) are highlighted.
              </p>
              <div className="kpi-row" style={{ display: 'flex', gap: 'var(--space-6)', marginBottom: 'var(--space-4)' }}>
                <div>
                  <h3 style={{ fontSize: 'var(--font-size-sm)' }}>Baseline (oldest-first)</h3>
                  <div className="kpi-row" style={{ display: 'flex', gap: 'var(--space-4)' }}>
                    <StatBlock label="Total Unmet" value={pp.baseline.totalUnmetQty} />
                    <StatBlock label="Priority-Weighted Unmet" value={pp.baseline.priorityWeightedUnmet} tone="danger" />
                  </div>
                </div>
                <div>
                  <h3 style={{ fontSize: 'var(--font-size-sm)' }}>Optimized (priority-scored)</h3>
                  <div className="kpi-row" style={{ display: 'flex', gap: 'var(--space-4)' }}>
                    <StatBlock label="Total Unmet" value={pp.optimized.totalUnmetQty} />
                    <StatBlock label="Priority-Weighted Unmet" value={pp.optimized.priorityWeightedUnmet} tone={improved ? 'success' : undefined} />
                  </div>
                </div>
              </div>
              {improved && (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-success)' }}>
                  The optimized ordering leaves less high-priority demand unmet for the same total
                  shortfall — same capacity, better-protected priorities.
                </p>
              )}
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <ShowCountSelect value={priorityShowCount} onChange={setPriorityShowCount} />
              </div>
              <div className="table-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>
                    {['Design', 'Priority Score', 'Aging', 'Rising?', 'Stock Urgent?'].map((h) => (
                      <th key={h} style={{ padding: '6px 4px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayedPriority.map((d) => (
                    <tr
                      key={d.design_id}
                      style={{
                        borderBottom: '1px solid var(--color-border)',
                        background: d.stockUrgent ? 'rgba(214, 69, 69, 0.08)' : 'transparent',
                      }}
                    >
                      <td style={{ padding: '6px 4px', fontSize: 'var(--font-size-sm)', fontWeight: d.stockUrgent ? 700 : 400 }}>{d.designName}</td>
                      <td className="data-figure" style={{ padding: '6px 4px', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>{d.priorityScore}</td>
                      <td className="data-figure" style={{ padding: '6px 4px', fontSize: 'var(--font-size-sm)' }}>{d.maxAgingDays}d</td>
                      <td style={{ padding: '6px 4px', fontSize: 'var(--font-size-sm)' }}>{d.isRising ? 'Yes' : '—'}</td>
                      <td style={{ padding: '6px 4px', fontSize: 'var(--font-size-sm)' }}>
                        {d.stockUrgent ? (
                          <span style={{ color: 'var(--color-danger)', fontWeight: 700 }}>⚠ Urgent</span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </Card>
          )}
        </>
      )}

      {!result && !running && (
        <Card>
          <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
            Click "Run optimization" to analyze safety stock and production priority.
          </p>
        </Card>
      )}
    </div>
  );
}
